import { useEffect, useRef } from 'react';

import { LogLevel } from 'services/logger.service';
import { signInForSSE } from 'services/auth.service';
import { useAppDispatch } from 'store/store-hooks';
import { appLogger } from 'components/app/_mediator/app-logger.slice';

const COUNTER = 100;
const WAIT: Milliseconds = 1000;
const ERR_ID = 'use-sse.ts';

enum SSE {
  off,
  connecting,
  listening,
  error,
}

interface Props<T, W> {
  pingCb: () => Promise<boolean>;
  subscribe: (token: string) => Promise<EventSource>;
  onProgress: (data: T) => Promise<{ closeSse: boolean } | undefined>;
  onQueue: (data: W[]) => void;
  onError?: (error: Event) => void;
}

export const useSse = <T, W>({ pingCb, subscribe, onProgress, onQueue, onError }: Props<T, W>) => {
  const eventSrcRef = useRef<EventSource | null>(null);
  const sse = useRef<SSE>(SSE.off);
  const dispatch = useAppDispatch();

  const onProgressRef = useRef(onProgress);
  const onQueueRef = useRef(onQueue);
  const onErrorRef = useRef(onError);

  onProgressRef.current = onProgress;
  onQueueRef.current = onQueue;
  onErrorRef.current = onError;

  const ping = async () => {
    return new Promise((resolve) => {
      pingCb()
        .then(() => resolve(true))
        .catch(() => resolve(false));
    }) as Promise<boolean>;
  };

  const isRunning = async (counter = COUNTER, wait = WAIT): Promise<boolean> => {
    const result: boolean = await new Promise((resolve) => {
      setTimeout(() => ping().then(resolve), wait);
    });
    if (result) return result;
    if (counter) return isRunning(--counter);
    return false;
  };

  const listen = async () => {
    if ([SSE.connecting, SSE.listening].includes(sse.current)) return;
    sse.current = SSE.connecting;
    if (!(await isRunning())) {
      const message = `[${ERR_ID}] listenSSE: ping: retry attempts exhausted`;
      dispatch(appLogger({ level: LogLevel.Info, error: { name: 'SSE', message } }));
      console.error(message);
      return;
    }
    sse.current = SSE.listening;
    eventSrcRef.current = await subscribe(await signInForSSE().then((res) => res.data.token));
    eventSrcRef.current?.addEventListener('notify', async (message) => {
      const data = JSON.parse((message as MessageEvent<string>).data) as T;
      const res = await onProgressRef.current(data);
      if (res && res.closeSse) close();
    });
    eventSrcRef.current?.addEventListener('queue', (message) => {
      const data = JSON.parse((message as MessageEvent<string>).data) as W[];
      onQueueRef.current(data);
    });
    eventSrcRef.current!.onerror = (error) => {
      const errorData = {
        error: error,
        sseState: sse.current,
        readyState: eventSrcRef.current?.readyState,
      };
      const message = `[${ERR_ID}] SSE connection error: ${error} | Data: ${JSON.stringify(errorData)}`;
      dispatch(appLogger({ level: LogLevel.Fatal, error: { name: 'SSE', message } }));
      sse.current = SSE.error;
      close();
      if (onErrorRef.current) onErrorRef.current(error);
    };
  };

  const close = () => {
    if (!eventSrcRef.current) return;
    eventSrcRef.current.close();
    eventSrcRef.current = null;
    sse.current = SSE.off;
  };

  useEffect(() => {
    return () => close();
  }, []);

  return { listen };
};
